import { LOCALE_CURRENCY, intlCurrencyFormatLocale } from "./locale-prefix";

const EMBEDDED_USD_AMOUNT = /\$([\d,.]+)([mbk]?)/gi;

export interface LocalizedAmountParts {
  symbol: string;
  amount: string;
  placement: "before" | "after";
}

export interface LocalizedCurrencyOptions {
  currency?: string;
  compact?: boolean;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

function formattingLocale(locale: string): string {
  return LOCALE_CURRENCY[locale] ? intlCurrencyFormatLocale(locale) : "en";
}

function currencyFormat(
  locale: string,
  currency = LOCALE_CURRENCY[locale] ?? "USD"
) {
  const parts = new Intl.NumberFormat(formattingLocale(locale), {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
  }).formatToParts(1);

  return {
    placement: parts[0]?.type === "currency" ? "before" : "after",
    symbol: parts.find((part) => part.type === "currency")?.value ?? "$",
  };
}

function groupSeparator(locale: string): string {
  return (
    new Intl.NumberFormat(formattingLocale(locale))
      .formatToParts(10_000)
      .find((part) => part.type === "group")?.value ?? ","
  );
}

function ensureFourDigitGrouping(
  value: number,
  formatted: string,
  locale: string
): string {
  const integer = Math.trunc(Math.abs(value));
  if (
    integer < 1_000 ||
    integer >= 10_000 ||
    /[^\d]/.test(formatted.slice(0, 4))
  ) {
    return formatted;
  }

  const integerText = integer.toString();
  if (!formatted.startsWith(integerText)) return formatted;
  return `${integerText[0]}${groupSeparator(locale)}${integerText.slice(1)}${formatted.slice(integerText.length)}`;
}

function formatLocalizedNumber(
  value: number,
  locale: string,
  options: Pick<
    LocalizedCurrencyOptions,
    "minimumFractionDigits" | "maximumFractionDigits"
  >
): string {
  const formatted = new Intl.NumberFormat(formattingLocale(locale), {
    minimumFractionDigits: options.minimumFractionDigits,
    maximumFractionDigits: options.maximumFractionDigits,
  }).format(value);
  return ensureFourDigitGrouping(value, formatted, locale);
}

function formatCompactNumber(value: number, locale: string): string {
  const units = [
    { threshold: 1_000_000_000, suffix: "b" },
    { threshold: 1_000_000, suffix: "m" },
    { threshold: 1_000, suffix: "k" },
  ];
  const unit = units.find(({ threshold }) => Math.abs(value) >= threshold);
  if (!unit) return formatLocalizedNumber(value, locale, {});

  const compactValue = value / unit.threshold;
  return `${formatLocalizedNumber(compactValue, locale, {
    maximumFractionDigits: compactValue % 1 === 0 ? 0 : 1,
  })}${unit.suffix}`;
}

/** Format a numeric amount using the current site's locale and currency. */
export function formatLocalizedCurrency(
  value: number,
  locale: string,
  options: LocalizedCurrencyOptions = {}
): string {
  const currency = options.currency ?? LOCALE_CURRENCY[locale] ?? "USD";
  const { placement, symbol } = currencyFormat(locale, currency);
  const amount = options.compact
    ? formatCompactNumber(value, locale)
    : formatLocalizedNumber(value, locale, options);

  return placement === "after" ? `${amount} ${symbol}` : `${symbol}${amount}`;
}

export function localizeCurrencyString(value: string, locale: string): string {
  const { placement, symbol } = currencyFormat(locale);

  const localized = value.replace(
    EMBEDDED_USD_AMOUNT,
    (_match, rawAmount: string, suffix: string) => {
      const decimalDigits = rawAmount.split(".")[1]?.length ?? 0;
      const amount = Number(rawAmount.replaceAll(",", ""));
      if (!Number.isFinite(amount)) return _match;

      if (suffix) {
        const compactAmount = `${formatLocalizedNumber(amount, locale, {
          minimumFractionDigits: decimalDigits,
          maximumFractionDigits: decimalDigits,
        })}${suffix.toLowerCase()}`;
        return placement === "after"
          ? `${compactAmount} ${symbol}`
          : `${symbol}${compactAmount}`;
      }

      return formatLocalizedCurrency(amount, locale, {
        minimumFractionDigits: decimalDigits,
        maximumFractionDigits: decimalDigits,
      });
    }
  );

  return symbol === "$" ? localized : localized.replaceAll("$", symbol);
}

export function localizeCurrency<T>(value: T, locale: string): T {
  if (typeof value === "string") {
    return localizeCurrencyString(value, locale) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => localizeCurrency(item, locale)) as T;
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        localizeCurrency(item, locale),
      ])
    ) as T;
  }
  return value;
}

/** Format a numeric amount with locale currency symbol and grouping. */
export function formatLocalizedAmountParts(
  value: number,
  locale: string,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): LocalizedAmountParts {
  const currency = LOCALE_CURRENCY[locale] ?? "USD";
  const formatLocale = formattingLocale(locale);
  const parts = new Intl.NumberFormat(formatLocale, {
    style: "currency",
    currency,
    minimumFractionDigits: options?.minimumFractionDigits ?? 2,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
  }).formatToParts(value);

  const symbol = parts.find((part) => part.type === "currency")?.value ?? "$";
  const placement = parts[0]?.type === "currency" ? "before" : "after";
  const amount = parts
    .filter(
      (part) =>
        part.type === "integer" ||
        part.type === "group" ||
        part.type === "decimal" ||
        part.type === "fraction"
    )
    .map((part) => part.value)
    .join("");

  return { symbol, amount, placement };
}
